
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import toast, { Toaster } from "react-hot-toast";
import { motion } from "framer-motion";
import "./Login.css";
import { signInWithGoogle } from "../components/firebase"

const Login = ({ setUser }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Forgot password state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetStep, setResetStep] = useState(1);

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      toast("You are already logged in!");
      navigate("/");
    }
  }, [navigate]);

  // Normal login
  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/auth/login", { email, password });
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      setUser(res.data.user);
      toast.success("Login successful!");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.message || "Login failed");
    }
  };

  // Google login
  const handleGoogleLogin = async () => {
    try {
      const googleUser = await signInWithGoogle();
      const name = googleUser.name || googleUser.displayName || "Google User";
      const res = await api.post("/auth/google", {
        email: googleUser.email,
        name,
        googleId: googleUser.uid,
      });

      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      setUser(res.data.user);
      toast.success("Google login successful!");
      navigate("/");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Google login failed");
    }
  };

  // Forgot password handler
  const handleForgot = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success("Reset link sent to your email");
      setResetStep(2);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send reset link");
    }
  };

  // Reset password handler
  const handleReset = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/reset-password", {
        email: forgotEmail,
        token: resetToken,
        password: newPassword,
      });
      toast.success("Password reset successful!");
      setShowForgot(false);
      setResetStep(1);
      setForgotEmail("");
      setResetToken("");
      setNewPassword("");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to reset password");
    }
  };

  return (
    <div className="login-wrapper">
      <Toaster position="top-center" reverseOrder={false} />
      <motion.div
        className="login-card"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-center mb-4"
        >
          Welcome Back
        </motion.h2>

        <form onSubmit={handleLogin}>
          <motion.div
            className="mb-3"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <label className="form-label">Email:</label>
            <input
              type="email"
              className="form-control"
              value={email}
              required
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
            />
          </motion.div>

          <motion.div
            className="mb-3 position-relative"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <label className="form-label">Password:</label>
            <input
              type={showPassword ? "text" : "password"}
              className="form-control"
              value={password}
              required
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
            />
            <span
              className="toggle-password"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? "🙈" : "👁️"}
            </span>
          </motion.div>

          <motion.button
            type="submit"
            className="btn btn-primary w-100"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            Login
          </motion.button>
        </form>

        {/* Divider */}
        <div className="text-center my-3 text-muted">OR</div>

        {/* Google Login Button */}
        <motion.button
          onClick={handleGoogleLogin}
          className="btn btn-outline-danger w-100 d-flex align-items-center justify-content-center gap-2"
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
        >
          <img
            src="https://www.svgrepo.com/show/355037/google.svg"
            alt="Google"
            className="me-2"
            style={{ width: "20px", height: "20px" }}
          />
          Continue with Google
        </motion.button>

        {/* Forgot Password Link */}
        <div className="text-center mt-3">
          <button
            className="btn btn-link"
            style={{ textDecoration: "underline" }}
            onClick={() => setShowForgot(true)}
          >
            Forgot Password?
          </button>
        </div>
      </motion.div>

      {/* Forgot Password Modal */}
      {showForgot && (
        <div className="modal-overlay">
          <div className="modal-body">
            <button
              className="modal-close"
              onClick={() => setShowForgot(false)}
            >
              ✕
            </button>
            {resetStep === 1 ? (
              <form onSubmit={handleForgot}>
                <h4>Forgot Password</h4>
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="form-control mb-2"
                  required
                />
                <button type="submit" className="btn btn-primary w-100">
                  Send Reset Link
                </button>
              </form>
            ) : (
              <form onSubmit={handleReset}>
                <h4>Reset Password</h4>
                <input
                  type="text"
                  placeholder="Enter token from email"
                  value={resetToken}
                  onChange={(e) => setResetToken(e.target.value)}
                  className="form-control mb-2"
                  required
                />
                <input
                  type="password"
                  placeholder="New password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="form-control mb-2"
                  required
                />
                <button type="submit" className="btn btn-success w-100">
                  Reset Password
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
